import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BasicModal from 'components/Modals/presets/BasicModal';
import { useTranslation } from 'react-i18next';
import {
  StyledWizardField,
  StyledSpaceBetween,
  StyledRightAlignedField,
} from 'components/Wizards/shared/styles';
import KizenTypography from 'app/kizentypo';
import Input from 'components/Kizen/Input';
import { DASHBOARD } from 'queries/query-keys';
import { useQuery } from '@tanstack/react-query';
import PermissionSettings from 'components/Sharing/PermissionSettings';
import { getDashboardQuery } from 'queries/dashboard';
import Validation from 'components/Inputs/Validation';
import { useFlashTransition } from 'hooks/useFlashState';
import useModal from 'components/Modals/useModal';
import Button from 'components/Button';
import Switch from 'components/Kizen/Switch';
import useField from 'hooks/useField';
import {
  DashboardStyleSettingsProvider,
  DashboardStyleSettings,
  DEFAULT_DASHLET_STYLE_SETTINGS,
} from 'ts-components/StyleSettings';
import { useVizCatColors } from 'pages/Dashboard/components/useVizCatColors';
import { isStyleConfigValid } from '../validation';
import { ApiNameInput } from './ApiNameInput';

const CreateLayoutModal = ({
  existing = {},
  onConfirm,
  canUserEditPermissions = true,
  entity = '',
  small,
  isDuplication,
  errors,
  blockHideIfDirty = false,
  onHide,
  showPrivateToggle = true,
  showEditAdminOptions,
  forceAdmin = false,
  ...others
}) => {
  const { t } = useTranslation();
  const defaultChartTheme = useVizCatColors();
  const [dashboardName, setDashboardName] = useState(existing.name ?? '');
  const [dashboardApiName, setDashboardApiName] = useState(
    existing.api_name ?? ''
  );
  const [apiNameError, setApiNameError] = useState(null);
  const [permissions, setPermissions] = useState();
  const [dirty, setDirty] = useState(false);
  const isEditing = useMemo(() => {
    return Object.keys(existing).length > 0;
  }, [existing]);
  const setDashboardNameWithDirtyState = useCallback((name) => {
    setDashboardName(name);
    setDirty(true);
  }, []);
  const setDashboardApiNameWithDirtyState = useCallback((name) => {
    setDashboardApiName(name);
    setDirty(true);
  }, []);
  const setDashboardPermissionsWithDirtyState = useCallback(
    (permissions, dirty) => {
      setPermissions(permissions);
      if (dirty) {
        setDirty(true);
      }
    },
    []
  );
  const dashboardQueryKey = useMemo(() => {
    return [...DASHBOARD.DASHBOARD, existing.id];
  }, [existing]);
  const handleFetchDashboard = useCallback(
    () => getDashboardQuery(existing.id),
    [existing.id]
  );
  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    isRefetching: dashboardRefetching,
  } = useQuery({
    queryKey: dashboardQueryKey,
    queryFn: handleFetchDashboard,
    enabled: !!existing.id,
  });

  const [isPrivate, setIsPrivate] = useField(
    dashboardData?.sharing_settings?.private ?? true
  );
  const setIsPrivateWithDirtyState = useCallback(
    (event) => {
      setIsPrivate(event.target.checked);
      setDirty(true);
    },
    [setIsPrivate]
  );

  const [styleSettings, setStyleSettings] = useState(
    dashboardData?.style_settings ?? {}
  );

  const [enableCustomizeStyle, setEnableCustomizeStyle] = useField(
    Object.keys(styleSettings ?? {}).length > 0
  );

  useEffect(() => {
    if (
      dashboardData?.style_settings &&
      Object.keys(dashboardData?.style_settings).length !== 0
    ) {
      setStyleSettings(dashboardData.style_settings ?? {});
      setEnableCustomizeStyle(true);
    }
  }, [
    dashboardData?.style_settings,
    dashboardLoading,
    setEnableCustomizeStyle,
  ]);

  const setEnableCustomizedStyles = useCallback(
    (event) => {
      setEnableCustomizeStyle(event.target.checked);
      setDirty(true);
      if (event.target.checked) {
        setStyleSettings({
          ...DEFAULT_DASHLET_STYLE_SETTINGS,
          chartTheme: defaultChartTheme,
        });
      } else {
        setStyleSettings({});
      }
    },
    [setStyleSettings, setEnableCustomizeStyle, defaultChartTheme]
  );

  const setStyleSettingsWithDirtyState = useCallback(
    (styleSettings) => {
      setStyleSettings(styleSettings);
      setDirty(true);
    },
    [setStyleSettings]
  );

  const handleConfirm = useCallback(() => {
    if (dashboardName) {
      onConfirm?.(
        {
          name: dashboardName,
          api_name: isEditing ? dashboardApiName : undefined,
          permissions,
          isPrivate,
          styleSettings: enableCustomizeStyle ? styleSettings : {},
        },
        isEditing,
        dashboardName !== existing.name,
        existing.id,
        isDuplication
      );
    }
  }, [
    styleSettings,
    dashboardName,
    enableCustomizeStyle,
    onConfirm,
    isEditing,
    existing,
    permissions,
    isDuplication,
    isPrivate,
    dashboardApiName,
  ]);

  const heading = useMemo(() => {
    if (isDuplication) {
      return `${t('Duplicate')} ${entity}`;
    }
    return isEditing ? `${t('Edit')} ${entity}` : `${t('Add')} ${entity}`;
  }, [entity, t, isDuplication, isEditing]);

  const nameInputRef = useRef(null);

  const [message, showMessage, flashErrorMessage] = useFlashTransition();

  useEffect(() => {
    if (errors?.name) {
      flashErrorMessage(errors.name);
    }
    if (errors?.api_name) {
      setApiNameError(errors.api_name);
    }
  }, [errors?.name, errors?.api_name, flashErrorMessage]);

  const [confirmUnsavedModalProps, confirmUnsavedModalTriggerProps] = useModal({
    handleSubmit: handleConfirm,
  });

  const handleCloseWithDirtyState = useCallback(() => {
    if (!dirty || !blockHideIfDirty) {
      onHide();
    } else {
      confirmUnsavedModalTriggerProps.onClick();
    }
  }, [dirty, onHide, blockHideIfDirty, confirmUnsavedModalTriggerProps]);

  const displayAPIname = !isDuplication && dashboardData;

  return (
    <>
      <BasicModal
        defaultLeftBtnText={t('Cancel')}
        heading={heading}
        size={small ? 'small' : 'medium'}
        disabled={
          !dashboardName?.trim() ||
          !isStyleConfigValid(styleSettings) ||
          !!apiNameError
        }
        buttonText={isDuplication ? t('Duplicate') : t('Save')}
        onConfirm={handleConfirm}
        onHide={handleCloseWithDirtyState}
        {...others}
      >
        <StyledSpaceBetween gap={10}>
          <StyledWizardField top={0} flex fullWidth>
            <KizenTypography>
              {t('{{entity}} Name', { entity })}
            </KizenTypography>
            <StyledWizardField top={12} flex>
              <Input
                value={dashboardName}
                onChange={setDashboardNameWithDirtyState}
                placeholder={t('{{entity}} Name', { entity })}
                error={errors?.name}
                ref={nameInputRef}
              />
              <Validation
                message={message}
                showMessage={showMessage}
                target={nameInputRef.current}
              />
            </StyledWizardField>
          </StyledWizardField>
          {displayAPIname && !small ? (
            <StyledRightAlignedField>
              <StyledWizardField fullWidth top={0}>
                <ApiNameInput
                  value={dashboardApiName}
                  onChange={setDashboardApiNameWithDirtyState}
                  label={t('API Name')}
                  placeholder={t('{{entity}} API Name', { entity })}
                  error={apiNameError}
                  setError={setApiNameError}
                />
              </StyledWizardField>
            </StyledRightAlignedField>
          ) : null}
        </StyledSpaceBetween>
        {displayAPIname && small ? (
          <StyledWizardField fullWidth top={20}>
            <ApiNameInput
              value={dashboardApiName}
              onChange={setDashboardApiNameWithDirtyState}
              label={t('API Name')}
              placeholder={t('{{entity}} API Name', { entity })}
              error={apiNameError}
              setError={setApiNameError}
            />
          </StyledWizardField>
        ) : null}
        <StyledSpaceBetween gap={10} top={20}>
          {!dashboardLoading && showPrivateToggle && canUserEditPermissions ? (
            <StyledWizardField fullWidth top={0}>
              <Switch
                data-qa="make-private-toggle"
                checked={isPrivate}
                textPlacement="regular-top"
                label={t('Make Private')}
                tooltip={t(
                  'Users will not be able to search for and request access to this {{instanceName}} unless granted View access or greater.',
                  { instanceName: entity }
                )}
                onClick={setIsPrivateWithDirtyState}
              />
            </StyledWizardField>
          ) : null}
          {!dashboardLoading && !dashboardRefetching ? (
            <StyledWizardField fullWidth top={0}>
              <Switch
                data-qa="customize-styles-toggle"
                checked={enableCustomizeStyle}
                textPlacement="regular-top"
                label={t('Customize Styles')}
                onClick={setEnableCustomizedStyles}
              />
            </StyledWizardField>
          ) : null}
        </StyledSpaceBetween>
        {enableCustomizeStyle && !dashboardLoading && !dashboardRefetching ? (
          <DashboardStyleSettingsProvider
            styleSettings={styleSettings ?? DEFAULT_DASHLET_STYLE_SETTINGS}
          >
            <DashboardStyleSettings onChange={setStyleSettingsWithDirtyState} />
          </DashboardStyleSettingsProvider>
        ) : null}
        {canUserEditPermissions ? (
          <PermissionSettings
            onChange={setDashboardPermissionsWithDirtyState}
            existing={dashboardData?.sharing_settings ?? {}}
            loading={dashboardLoading || dashboardRefetching}
            exclusive={false}
            owner={existing.owner}
            showEditAdminOptions={showEditAdminOptions}
            isDuplication={isDuplication}
            forceAdmin={forceAdmin}
          />
        ) : null}
      </BasicModal>
      <BasicModal
        className={`no-drag`}
        heading={t('You Have Unsaved Changes')}
        buttonText={t('Discard Changes')}
        defaultLeftBtnText={t('Cancel')}
        actionBtnColor="red"
        leftBtn={<Button variant="text" color="blue" />}
        {...confirmUnsavedModalProps}
        onConfirm={() => {
          onHide();
        }}
        defaultLeftBtnHandler={confirmUnsavedModalTriggerProps.hide}
      >
        {t('Unsaved changes will be lost, would you like to continue?')}
      </BasicModal>
    </>
  );
};
export default CreateLayoutModal;
